import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Navbar } from '@/components/Navbar';
import { SimpleAddressAutocomplete } from '@/components/GooglePlacesAutocomplete';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register({ setUser }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    address: '',
    phone: '',
    email: '',
    password: '',
    confirm_password: '',
    client_type: 'Broker',
    has_rate_agreement: false,
    previous_customer: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        company_name: formData.company_name,
        individual_name: `${formData.first_name} ${formData.last_name}`.trim(),
        first_name: formData.first_name,
        last_name: formData.last_name,
        company_address: formData.address,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        client_type: formData.client_type,
        has_rate_agreement: formData.has_rate_agreement,
        previous_customer: formData.previous_customer,
        country: 'USA'
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, payload);
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      setUser(response.data.user);
      toast.success('Registration successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar />
      
      <div className="px-6 py-12" data-testid="register-page">
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 md:p-8">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase mb-2 text-center">
              <span className="text-[#00A6E8]">CLIENT</span>
              <span className="text-white"> REGISTRATION</span>
            </h1>
            <p className="text-slate-400 text-center mb-8">Create your DLT logistics portal account</p>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
              {/* Company Information */}
              <div>
                <Label htmlFor="company_name" className="text-slate-300">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  required
                  data-testid="company-name-input"
                />
              </div>
              
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-slate-300">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    data-testid="first-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="last_name" className="text-slate-300">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>
              
              {/* Address with Google Places Autocomplete */}
              <div>
                <Label htmlFor="address" className="text-slate-300">Address *</Label>
                <SimpleAddressAutocomplete
                  value={formData.address}
                  onChange={(address) => setFormData({...formData, address})}
                  placeholder="Start typing your address..."
                  required={true}
                  testId="address-input"
                />
              </div>
              
              {/* Contact Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-slate-300">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    placeholder="(555) 123-4567"
                    data-testid="phone-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-slate-300">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              {/* Password */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password" className="text-slate-300">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirm_password" className="text-slate-300">Confirm Password *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              {/* Customer Type */}
              <div>
                <Label htmlFor="client_type" className="text-slate-300">Customer Type *</Label>
                <Select value={formData.client_type} onValueChange={(value) => setFormData({...formData, client_type: value})}>
                  <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="client-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                    <SelectItem value="Broker">Broker</SelectItem>
                    <SelectItem value="Freight Forwarder">Freight Forwarder</SelectItem>
                    <SelectItem value="Direct Shipper">Direct Shipper</SelectItem>
                    <SelectItem value="Private Individual">Private Individual</SelectItem>
                    <SelectItem value="Overseas Agent">Overseas Agent</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Rate Agreement */}
              <div>
                <Label className="text-slate-300 mb-3 block">Do you have a rate agreement with DLT?</Label>
                <RadioGroup 
                  value={formData.has_rate_agreement ? 'yes' : 'no'} 
                  onValueChange={(value) => setFormData({...formData, has_rate_agreement: value === 'yes'})}
                  className="flex gap-6"
                  data-testid="rate-agreement-radio"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="rate-yes" className="border-white/30" />
                    <Label htmlFor="rate-yes" className="text-slate-300 cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="rate-no" className="border-white/30" />
                    <Label htmlFor="rate-no" className="text-slate-300 cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Previous Customer */}
              <div>
                <Label className="text-slate-300 mb-3 block">Have you worked with DLT before?</Label>
                <RadioGroup 
                  value={formData.previous_customer ? 'yes' : 'no'} 
                  onValueChange={(value) => setFormData({...formData, previous_customer: value === 'yes'})}
                  className="flex gap-6"
                  data-testid="previous-customer-radio"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="prev-yes" className="border-white/30" />
                    <Label htmlFor="prev-yes" className="text-slate-300 cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="prev-no" className="border-white/30" />
                    <Label htmlFor="prev-no" className="text-slate-300 cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>
              
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#00A6E8] text-[#0A0E1A] font-bold uppercase tracking-wider hover:bg-[#33B8ED] hover:scale-105 transition-all py-6"
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-slate-400">
                Already have an account?{' '}
                <Link to="/login" className="text-[#00A6E8] hover:text-[#33B8ED] font-semibold">
                  Login here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
